import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import { InkedTitle } from '../components/InkedTitle';
import { Reveal } from '../components/Reveal';
import { WorkCard } from '../components/WorkCard';
import { styleLabels, tattooStyles, type TattooStyle, works } from '../data';
import { useLang } from '../hooks/useLang';
import { cn } from '../lib/cn';

const STYLE_PARAM = 'stile';

export function WorksPage() {
  const { t } = useTranslation();
  const lang = useLang();
  const [searchParams, setSearchParams] = useSearchParams();

  const activeStyle = (searchParams.get(STYLE_PARAM) as TattooStyle | null) ?? null;

  const filtered = useMemo(
    () => (activeStyle ? works.filter((w) => w.style === activeStyle) : works),
    [activeStyle],
  );

  const setStyle = (style: TattooStyle | null) => {
    const next = new URLSearchParams(searchParams);
    if (style) next.set(STYLE_PARAM, style);
    else next.delete(STYLE_PARAM);
    setSearchParams(next, { replace: true });
  };

  return (
    <>
      <section className="px-6 pt-36 pb-12 md:px-10 md:pt-48 md:pb-20">
        <div className="mx-auto max-w-[1800px] grid gap-8 md:grid-cols-12 items-end">
          <div className="md:col-span-12">
            <p className="text-base font-display lowercase text-ink-muted tracking-tight">
              {t('work.headerEyebrow').toLowerCase()}
            </p>
            <InkedTitle
              as="h1"
              immediate
              delay={0.15}
              className="mt-6 font-display tracking-display leading-[0.85] text-[clamp(3.5rem,12vw,13rem)] lowercase"
            >
              {t('work.headerTitle')}
            </InkedTitle>
          </div>
          <p className="md:col-span-7 md:col-start-2 text-lg md:text-xl text-ink-paper/75 max-w-2xl">
            {t('work.headerLead')}
          </p>
        </div>
      </section>

      <section className="sticky top-16 md:top-20 z-20 bg-ink-base/95 backdrop-blur-md border-y border-ink-line/40">
        <div className="px-6 md:px-10 py-4 flex items-center gap-6 overflow-x-auto">
          <span className="text-base font-display lowercase text-ink-muted shrink-0">
            {t('common.filter').toLowerCase()} ↓
          </span>
          <div className="flex items-center gap-1 shrink-0">
            <FilterPill active={!activeStyle} onClick={() => setStyle(null)}>
              {t('work.filterAll').toLowerCase()} ({works.length})
            </FilterPill>
            {tattooStyles.map((style) => {
              const count = works.filter((w) => w.style === style).length;
              if (count === 0) return null;
              return (
                <FilterPill
                  key={style}
                  active={activeStyle === style}
                  onClick={() => setStyle(style)}
                >
                  {styleLabels[style][lang].toLowerCase()} ({count})
                </FilterPill>
              );
            })}
          </div>
        </div>
      </section>

      <section className="px-6 py-16 md:px-10 md:py-24">
        {filtered.length === 0 ? (
          <p className="font-mono text-sm uppercase tracking-[0.25em] text-ink-muted">
            {t('work.empty')}
          </p>
        ) : (
          <div className="grid gap-x-6 gap-y-12 md:gap-x-8 md:gap-y-16 grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
            {filtered.map((work, i) => (
              <Reveal
                key={work.id}
                delay={(i % 4) * 0.05}
                className={cn(
                  i % 5 === 0 && 'md:translate-y-12',
                  i % 5 === 2 && 'md:-translate-y-8',
                )}
              >
                <WorkCard work={work} index={i} />
              </Reveal>
            ))}
          </div>
        )}
      </section>
    </>
  );
}

function FilterPill({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'whitespace-nowrap text-base lowercase font-display tracking-display px-4 py-2 border transition-colors',
        active
          ? 'border-ink-paper bg-ink-paper text-ink-base'
          : 'border-ink-line/60 text-ink-paper/75 hover:border-ink-paper hover:text-ink-paper',
      )}
    >
      {children}
    </button>
  );
}
